import { useState } from "react";
import { RotateCcw, TrashIcon } from "lucide-react";
import { useForm } from "react-hook-form";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { Button } from "../../../Components/Buttons";
import { ToastMessage, getSelectedIds } from "../../../Components/Utils";
import { handleError } from "../../../stores/components/handleError";
import {
  useFetchSocialLinksQuery,
  useFetchTrashedSocialLinksQuery,
  useCreateSocialLinkMutation,
  useUpdateSocialLinkMutation,
  useDeleteSocialLinkMutation,
  useDestroySocialLinkMutation,
  useRestoreSocialLinkMutation,
  useBulkDeleteSocialLinksMutation,
  useBulkDestroySocialLinksMutation,
  useBulkRestoreSocialLinksMutation,
} from "../../../stores/api/socialLinks.api";
import socialLinksHelper from "../../../stores/api/helpers/socialLinks.helper";
import payloadHelper from "../../../stores/api/helpers/payload.helper";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const HEADERS = [
  { key: "name", label: "Platform" },
  { key: "icon", label: "Icon" },
  { key: "link", label: "URL" },
  {
    key: "status",
    label: "Status",
    type: "status",
  },
];

const SocialLinkForm = ({ defaultValues = {}, onSave, onCancel }) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: defaultValues.name ?? "",
      icon: defaultValues.icon ?? "",
      link: defaultValues.link ?? "",
      status: defaultValues.status ?? "Active",
    },
  });
  const status = watch("status");

  return (
    <form
      onSubmit={handleSubmit(onSave)}
      onKeyDown={(e) => e.key === "Escape" && onCancel()}
      className="w-full grid grid-cols-1 sm:grid-cols-[1fr_1fr_2fr_auto_auto] gap-3 items-start"
    >
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Platform</label>
        <input
          autoFocus
          {...register("name", { required: "Required", validate: (v) => v.trim().length > 0 || "Required" })}
          placeholder="e.g. Facebook"
          className="border border-gray-300 dark:border-slate-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-slate-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition w-full"
        />
        {errors.name && <span className="text-xs text-red-500">{errors.name.message}</span>}
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Icon</label>
        <input
          {...register("icon", { required: "Required" })}
          placeholder="e.g. facebook"
          className="border border-gray-300 dark:border-slate-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-slate-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition w-full"
        />
        {errors.icon && <span className="text-xs text-red-500">{errors.icon.message}</span>}
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">URL</label>
        <input
          {...register("link", { required: "Required" })}
          placeholder="https://..."
          className="border border-gray-300 dark:border-slate-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-slate-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition w-full"
        />
        {errors.link && <span className="text-xs text-red-500">{errors.link.message}</span>}
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</label>
        <button
          type="button"
          onClick={() => setValue("status", status === "Active" ? "Inactive" : "Active")}
          className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold border transition-colors cursor-pointer
            ${
              status === "Active"
                ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-500/10 dark:text-white-300 dark:border-green-500/30"
                : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 dark:bg-gray-700/20 dark:text-gray-400 dark:border-gray-600"
            }`}
        >
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${status === "Active" ? "bg-green-500" : "bg-gray-400"}`} />
          {status}
        </button>
        <input type="hidden" {...register("status")} />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-transparent select-none">·</label>
        <div className="flex gap-2">
          <button type="submit" className="px-4 py-1.5 text-xs font-semibold rounded-md bg-green-500 text-white hover:bg-green-600 transition">Save</button>
          <button type="button" onClick={onCancel} className="px-4 py-1.5 text-xs font-semibold rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600 transition">Cancel</button>
        </div>
      </div>
    </form>
  );
};

const SocialLinkList = () => {
  const [trashed, setTrashed] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const queryParams = payloadHelper.forList({
    page,
    limit,
    search,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });

  const activeQuery = useFetchSocialLinksQuery(queryParams, { skip: trashed });
  const trashedQuery = useFetchTrashedSocialLinksQuery(queryParams, { skip: !trashed });
  const { data: links = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;

  const [createLink] = useCreateSocialLinkMutation();
  const [updateLink] = useUpdateSocialLinkMutation();
  const [deleteLink] = useDeleteSocialLinkMutation();
  const [destroyLink] = useDestroySocialLinkMutation();
  const [restoreLink] = useRestoreSocialLinkMutation();
  const [bulkDelete] = useBulkDeleteSocialLinksMutation();
  const [bulkDestroy] = useBulkDestroySocialLinksMutation();
  const [bulkRestore] = useBulkRestoreSocialLinksMutation();

  const resetPage = () => setPage(1);

  const handleCreateSave = async (data) => {
    try {
      await createLink(socialLinksHelper.forCreate(data)).unwrap();
      ToastMessage({ type: "success", title: "Created!", message: `"${data.name}" added.` });
    } catch (err) {
      handleError(err, "Failed to create.");
    }
  };

  const handleEditSave = async (data, row) => {
    try {
      await updateLink(socialLinksHelper.forUpdate(row.id, data)).unwrap();
      ToastMessage({ type: "success", title: "Updated!", message: `"${data.name}" updated.` });
    } catch (err) {
      handleError(err, "Failed to update.");
    }
  };

  const handleSingle = (row, type = "delete") => {
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: `${verb} Link`,
      message: `${verb} "${row.name}"?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") await restoreLink(row.id).unwrap();
          else if (type === "destroy") await destroyLink(row.id).unwrap();
          else await deleteLink(row.id).unwrap();
          ToastMessage({ type: "success", title: "Done!", message: `Link ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed.");
        }
      },
    });
  };

  const handleBulk = (selectedItems, type = "delete") => {
    const ids = getSelectedIds(selectedItems);
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: "Bulk Action",
      message: `${verb} ${ids.length} link(s)?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(ids);
          if (type === "restore") await bulkRestore(body).unwrap();
          else if (type === "destroy") await bulkDestroy(body).unwrap();
          else await bulkDelete(body).unwrap();
          ToastMessage({ type: "success", title: "Done!", message: `${ids.length} link(s) ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };

  return (
    <>
      <BreadCrumb title="Social Links" />
      <TableListing
        headers={HEADERS}
        paginationData={links}
        loading={isFetching}
        handleBulk={handleBulk}
        statusModule="social-link"
        search={search}
        setSearch={setSearch}
        trashed={trashed}
        setTrashed={setTrashed}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
        inlineEdit={
          !trashed
            ? (row, _onSave, onCancel) => (
                <SocialLinkForm
                  defaultValues={row}
                  onSave={(data) => {
                    handleEditSave(data, row);
                    onCancel();
                  }}
                  onCancel={onCancel}
                />
              )
            : null
        }
        inlineCreate={
          !trashed
            ? (_onSave, onCancel) => (
                <SocialLinkForm
                  onSave={(data) => {
                    handleCreateSave(data);
                    onCancel();
                  }}
                  onCancel={onCancel}
                />
              )
            : null
        }
        onInlineEditSave={() => {}}
        onInlineCreateSave={() => {}}
        actions={(row) =>
          trashed ? (
            <div className="flex gap-2">
              <Button
                onClick={() => handleSingle(row, "restore")}
                title="Restore"
                icon={RotateCcw}
                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
              />
              <Button
                onClick={() => handleSingle(row, "destroy")}
                title="Destroy"
                icon={TrashIcon}
                className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
              />
            </div>
          ) : (
            <Button
              onClick={() => handleSingle(row, "delete")}
              title="Delete"
              icon={TrashIcon}
              className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
            />
          )
        }
      />
    </>
  );
};

export default SocialLinkList;
